import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { IconButton, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress, Divider, Typography } from '@mui/material';
import { SwapHoriz as SwitchIcon, Check as CheckIcon } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// A node-level role (region/organisation/enterprise admin) is only the same membership
// when it names the same node too — somebody who administers two regions holds two.
const sameMembership = (a, b) =>
  a && b && a.role === b.role
  && (a.clinic_id || null) === (b.clinic_id || null)
  && (a.node_id || null) === (b.node_id || null);

/** "Switch Portal" control for the patient header. Hidden unless the identity holds
 *  more than one portal. */
export default function PortalSwitcher() {
  const [memberships, setMemberships] = useState([]);
  const [active, setActive] = useState(null);
  const [anchor, setAnchor] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API_URL}/api/v1/auth/memberships`)
      .then((res) => {
        if (!alive) return;
        setMemberships(res.data.memberships || []);
        setActive(res.data.active || null);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (memberships.length < 2) return null;

  const go = async (m) => {
    if (sameMembership(m, active)) { setAnchor(null); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/v1/auth/switch-portal`, {
        role: m.role,
        clinic_id: m.clinic_id || null,
        // A region, organisation or enterprise admin is scoped to a node, and the
        // server refuses a switch that doesn't name which one. Leaving it out was why
        // switching BACK to such a role came back 403. `organization_id` is the older
        // name for the same thing and is still what an org_admin switch is read under.
        node_id: m.node_id || null,
        organization_id: m.organization_id || (m.role === 'org_admin' ? m.node_id : null) || null,
      });
      window.location.assign(res.data.handoff_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not switch portals');
      setBusy(false);
      setAnchor(null);
    }
  };

  return (
    <>
      <Tooltip title="Switch portal">
        <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} disabled={busy}>
          {busy ? <CircularProgress size={18} /> : <SwitchIcon sx={{ color: 'text.secondary' }} />}
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.secondary', display: 'block' }}>
          Your portals
        </Typography>
        <Divider />
        {memberships.map((m) => {
          const isActive = sameMembership(m, active);
          return (
            <MenuItem key={`${m.role}:${m.clinic_id || ''}:${m.node_id || ''}`} selected={isActive} onClick={() => go(m)}>
              <ListItemIcon>{isActive ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
              <ListItemText primary={m.label} secondary={isActive ? 'Current' : null} />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
